import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useUpgradeModal } from '../contexts/UpgradeModalContext';
import { useSubscriptionTier } from '../contexts/SubscriptionTierContext';
import { useAuth } from '../contexts/AuthContext';
import type { MedicalRecord, RecordType } from '../types';
import { BASIC_VAULT_MAX_DOCUMENTS } from '../utils/constants';
import { countMedicalRecordsForUser } from '../utils/medicalRecordCount';
import { UpgradeRequiredError } from '../utils/upgradeErrors';

// ─── Types ───────────────────────────────────────────────────────────────────

/** Shape returned by PostgREST for `public.medical_records` (see 000_create_tables.sql). */
interface MedicalRecordRow {
  id: string;
  pet_id: string;
  record_type: string;
  title: string;
  description: string | null;
  document_url: string | null;
  visit_date: string;
  vet_name: string | null;
  clinic_name: string | null;
  extracted_text?: string | null;
}

function recordFromRow(row: MedicalRecordRow): MedicalRecord {
  return {
    id: row.id,
    pet_id: row.pet_id,
    record_type: row.record_type as RecordType,
    document_url: row.document_url ?? '',
    extracted_text: row.extracted_text ?? null,
    record_date: row.visit_date,
  };
}

function uploadTitle(record_type: RecordType, fileName: string): string {
  const label =
    record_type === 'vaccine'
      ? 'Vaccination'
      : record_type === 'prescription'
        ? 'Prescription'
        : 'Lab report';
  const stem = fileName.replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9._\-\s]/g, '_').slice(0, 80);
  return stem ? `${label} · ${stem}` : label;
}

export interface NewRecordPayload {
  pet_id: string;
  record_type: RecordType;
  record_date: string; // ISO 8601
  /** Raw image/PDF file to upload to Supabase Storage */
  file: File;
}

interface UseMedicalRecordsReturn {
  records: MedicalRecord[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  /**
   * Upload a document file to Storage and create a medical_record row.
   * Storage path: {user_id}/{pet_id}/{timestamp}-{filename}
   */
  addRecord: (payload: NewRecordPayload) => Promise<MedicalRecord>;
  /**
   * Generate a short-lived signed URL for viewing a private document.
   * Signed URLs expire after 60 minutes.
   */
  getSignedUrl: (storagePath: string) => Promise<string | null>;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

/**
 * useMedicalRecords — fetch and create medical records for a single pet.
 *
 * All Storage uploads follow the path pattern:
 *   documents/{user_id}/{pet_id}/{timestamp}-{filename}
 *
 * This pattern is enforced by the storage RLS policies in
 * supabase/migrations/001_storage_rls.sql.
 */
export function useMedicalRecords(petId: string): UseMedicalRecordsReturn {
  const { user } = useAuth();
  const { tier, ready } = useSubscriptionTier();
  const { showUpgrade } = useUpgradeModal();

  const [records, setRecords] = useState<MedicalRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRecords = useCallback(async () => {
    if (!user || !petId) return;

    setIsLoading(true);
    setError(null);

    const { data, error: fetchError } = await supabase
      .from('medical_records')
      .select('*')
      .eq('pet_id', petId)
      .order('visit_date', { ascending: false });

    if (fetchError) {
      setError(fetchError.message);
    } else {
      setRecords((data as MedicalRecordRow[] | null)?.map(recordFromRow) ?? []);
    }
    setIsLoading(false);
  }, [user, petId]);

  useEffect(() => {
    void fetchRecords();
  }, [fetchRecords]);

  // ── addRecord ──────────────────────────────────────────────────────────────

  const addRecord = useCallback(
    async ({ pet_id, record_type, record_date, file }: NewRecordPayload): Promise<MedicalRecord> => {
      if (!user) throw new Error('Not authenticated');

      if (ready && tier === 'basic') {
        const total = await countMedicalRecordsForUser(user.id);
        if (total >= BASIC_VAULT_MAX_DOCUMENTS) {
          showUpgrade('documents');
          throw new UpgradeRequiredError('documents');
        }
      }

      // Build storage path — RLS policy checks the first folder segment = user_id
      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `${user.id}/${pet_id}/${timestamp}-${safeName}`;

      // 1. Upload file to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: file.type,
        });

      if (uploadError) throw new Error(`Upload failed: ${uploadError.message}`);

      // 2. Insert medical_record row with the storage path (not a public URL)
      const { data, error: insertError } = await supabase
        .from('medical_records')
        .insert({
          pet_id,
          record_type,
          visit_date: record_date,
          title: uploadTitle(record_type, file.name),
          description: null,
          document_url: storagePath,
        })
        .select()
        .single();

      if (insertError) throw new Error(insertError.message);

      const created = recordFromRow(data as MedicalRecordRow);
      setRecords((prev) => [created, ...prev]);
      return created;
    },
    [user, ready, tier, showUpgrade]
  );

  // ── getSignedUrl ───────────────────────────────────────────────────────────

  const getSignedUrl = useCallback(
    async (storagePath: string): Promise<string | null> => {
      const { data, error: signError } = await supabase.storage
        .from('documents')
        .createSignedUrl(storagePath, 60 * 60); // 1-hour expiry

      if (signError) {
        console.error('[useMedicalRecords] signed URL error:', signError.message);
        return null;
      }
      return data.signedUrl;
    },
    []
  );

  return { records, isLoading, error, refetch: fetchRecords, addRecord, getSignedUrl };
}
